/**
 * @file has-feature.js
 * @description Directiva Vue `v-has-feature` análoga a `v-has-permission`,
 *              pero con semántica de feature flags por tenant.
 *
 *              SEMÁNTICA DEL DEFAULT — feature ausente en
 *              `Organization.features` ⇒ ENCENDIDA. Para apagar, el
 *              platform-admin guarda `false` explícito desde el tablero.
 *
 *              Lee las features del organizationContext del store (hidratado
 *              por la action `loadOrganizationContext` que pega a
 *              `GET /organization/me` al login):
 *                `store.state.organizationContext.features`
 *
 *              Fallback secundario: `store.state.user.organization.features`
 *              por si algún flujo legacy populate la org dentro del user.
 *
 *              Si la feature está APAGADA, remueve el elemento del DOM (el
 *              mismo patrón que v-if).
 *
 * @example
 *   button(v-has-feature="'ooss.validator'") Validar afiliado
 *
 *              IMPORTANTE: esta directiva NO se aplica todavía a ningún item
 *              de UI existente. Es infra para fases siguientes. Cuando
 *              quieras esconder un menú/sección de Vilella, agregale
 *              `v-has-feature="'<clave>'"` y prendé/apagá desde el tablero.
 */

import store from "@/store";

/**
 * @description Resuelve si una feature está encendida en el tenant actual,
 *              con fallback default-ON cuando la clave no está seteada.
 * @param {string} key
 * @returns {boolean}
 */
function isEnabled(key) {
  // Fuente principal: /organization/me (hidratado al login). Las features
  // se persisten en localStore así que sobreviven al refresh.
  const ctxFeatures =
    store.state.organizationContext && store.state.organizationContext.features;
  // Fallback legacy: si algún flujo populate org dentro del user, también
  // miramos ahí. En la mayoría de los casos no existe (organization en el
  // JWT viene como string ID).
  const userFeatures =
    store.state.user &&
    store.state.user.organization &&
    typeof store.state.user.organization === "object" &&
    store.state.user.organization.features;
  const features = ctxFeatures || userFeatures;
  if (!features) return true;
  // Soporta dot.notation: "ooss.providers" → features["ooss"]["providers"]
  // (porque Mongoose Maps se serializan como objeto anidado en el endpoint).
  let value;
  if (key.indexOf(".") !== -1) {
    const [group, sub] = key.split(".");
    const groupObj = features[group];
    value =
      groupObj && typeof groupObj === "object" ? groupObj[sub] : undefined;
    // Si no está nested, fallback a la clave flat por compatibilidad con
    // configs viejas que guardaban "ooss.providers" como string key.
    if (value === undefined) value = features[key];
  } else {
    value = features[key];
  }
  if (value === undefined || value === null) return true;
  return Boolean(value);
}

/**
 * @description Aplica el estado de visibilidad de el segun la feature.
 * @param {HTMLElement} el
 * @param {{ value: string }} binding
 */
function applyVisibility(el, binding) {
  const key = binding.value;
  if (!key) return;
  if (!isEnabled(key)) {
    el.style.display = "none";
    el.setAttribute("data-feature-off", key);
  } else {
    el.style.display = "";
    el.removeAttribute("data-feature-off");
  }
}

const hasFeature = {
  bind(el, binding) {
    applyVisibility(el, binding);
    // UC-REG-074: la directiva NO es reactiva al store por default — bind
    // y update solo se disparan cuando el VNode se re-renderiza. Si
    // organizationContext se hidrata DESPUES del mount del componente
    // (caso refresh con token activo + race condition del login pre-await),
    // los elementos quedaban con el display calculado contra organizationContext
    // = null. Suscribimos al store y re-aplicamos cuando organizationContext
    // cambia. Cleanup en unbind.
    // eslint-disable-next-line no-param-reassign
    el.__hasFeatureUnwatch = store.watch(
      (state) =>
        state.organizationContext && state.organizationContext.features,
      () => applyVisibility(el, binding),
      { deep: true }
    );
  },
  update(el, binding) {
    applyVisibility(el, binding);
  },
  unbind(el) {
    if (el.__hasFeatureUnwatch) {
      el.__hasFeatureUnwatch();
      // eslint-disable-next-line no-param-reassign
      delete el.__hasFeatureUnwatch;
    }
  },
};

export default hasFeature;
